import {
  useCombobox,
  UseComboboxStateChange,
  useMultipleSelection,
  UseMultipleSelectionReturnValue,
  UseMultipleSelectionStateChange,
} from 'downshift'
import { VariableSizeList } from 'react-window'
import { ComboBoxGetOptionProperty, OPTION_TYPE_CREATE } from './utils'

/**
 * useMultipleSelection : onStateChange custom controller
 * @see https://www.downshift-js.com/use-multiple-selection/
 * @see https://github.com/downshift-js/downshift/tree/master/src/hooks/useMultipleSelection#onstatechange
 */
export const mutipleSelectionStateController = (
  changes: UseMultipleSelectionStateChange<any>,
  limitChips: number,
  reset: () => void,
) => {
  switch (changes.type) {
    // backspace reset selected items if aggregated in a single chip
    case useMultipleSelection.stateChangeTypes.DropdownKeyDownBackspace:
      if (limitChips === 1) reset()
      break
    default:
      break
  }
}

/**
 * useComboBox : onStateChange custom controller
 * @see https://www.downshift-js.com/use-combobox
 * @see https://github.com/downshift-js/downshift/tree/master/src/hooks/useCombobox#onstatechange
 */
export const comboboxStateController = <T>(
  changes: UseComboboxStateChange<any>,
  multipleSelectionValues: UseMultipleSelectionReturnValue<T>,
  allowVirtualization: boolean,
  getOptionLabel: ComboBoxGetOptionProperty<T>,
  isOpen: boolean,
  isOptionSelected: <T>(selectedItems: T[], option: T, getOptionLabel: Function) => boolean,
  keepInputOnSelect: boolean | undefined,
  options: any,
  multiple: boolean,
  onCreate: ((value: string) => void) | undefined,
  setInputValue: React.Dispatch<React.SetStateAction<string | undefined>>,
  setOptions: (newValue: any) => void,
  virtualList: VariableSizeList<any> | null,
) => {
  const { highlightedIndex, inputValue, type, selectedItem } = changes
  const { addSelectedItem, removeSelectedItem, selectedItems, setSelectedItems } = multipleSelectionValues
  let newSelectedItem = selectedItem

  switch (type) {
    case useCombobox.stateChangeTypes.InputChange:
      setInputValue(inputValue)
      // clear selected item in single mode
      if (!multiple && selectedItems.length === 1) {
        removeSelectedItem(selectedItems[0])
      }
      break
    case useCombobox.stateChangeTypes.InputKeyDownEnter:
    case useCombobox.stateChangeTypes.ItemClick:
      if (newSelectedItem && newSelectedItem.__type === OPTION_TYPE_CREATE) {
        if (onCreate) {
          onCreate(newSelectedItem.value)
        } else {
          // uncontrolled mode > update list of options internally
          newSelectedItem = {
            value: newSelectedItem.value,
            label: newSelectedItem.value,
          }
          setOptions([...options, newSelectedItem])
        }
      }
      // new selected item
      if (newSelectedItem && inputValue !== '') {
        if (!multiple) {
          setInputValue(getOptionLabel(newSelectedItem))
          setSelectedItems([newSelectedItem])
        } else {
          // clear input in multiple mode
          if (!keepInputOnSelect) {
            setInputValue('')
          }
          if (isOptionSelected(selectedItems, newSelectedItem, getOptionLabel)) {
            removeSelectedItem(newSelectedItem)
          } else {
            addSelectedItem(newSelectedItem)
          }
        }
      }
      break
    case useCombobox.stateChangeTypes.InputKeyDownArrowDown:
    case useCombobox.stateChangeTypes.InputKeyDownArrowUp:
      if (!allowVirtualization || !isOpen || highlightedIndex === undefined) return
      // loop keyboard navigation when using virtualisation
      virtualList?.scrollToItem(highlightedIndex)
      break
    default:
      break
  }
}
